import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, defer, lastValueFrom, throwError } from 'rxjs';

import { ProfileNotReadyError } from '../auth/profile-not-ready.error';
import type { AccountMembership } from './team.types';
import type { Account, MeUser, UpdateProfilePayload } from '../auth/auth.types';
import { ApiClient } from './api-client';
import { expand, joinQuery } from './api-url.helpers';

/**
 * Resource API for `/v1/users/*`.
 *
 * Hand-written for Phase A; will be regenerated from the OpenAPI spec once
 * the generator step is in place (see migration-docs/architecture-proposal.md).
 */
@Injectable({ providedIn: 'root' })
export class UsersApi {
  private readonly api = inject(ApiClient);

  /** `GET /v1/users/me?expand[]=country` — the Phase A smoke test endpoint. */
  me(options: { expand?: readonly string[] } = {}): Promise<MeUser> {
    const query = joinQuery(expand(options.expand ?? ['country']));
    return lastValueFrom(this.api.get<MeUser>(`/users/me${query}`));
  }

  meWithAccounts(): Promise<MeUser & { accounts: readonly Account[] }> {
    const query = joinQuery(expand(['accounts']));
    return lastValueFrom(
      this.api.get<MeUser & { accounts: readonly Account[] }>(`/users/me${query}`),
    );
  }

  meWithAccountMemberships(): Promise<
    MeUser & { accountMemberships: readonly AccountMembership[] }
  > {
    const query = joinQuery(expand(['account_memberships']));
    return lastValueFrom(
      this.api.get<MeUser & { accountMemberships: readonly AccountMembership[] }>(
        `/users/me${query}`,
      ),
    );
  }

  /**
   * `PATCH /users/me` — update mutable profile fields. The case-mapper
   * interceptor converts the camelCase payload to snake_case on the wire,
   * so callers pass `firstName`/`lastName`/`jobTitle`/`growzzRole`.
   */
  update(patch: UpdateProfilePayload): Promise<MeUser> {
    return lastValueFrom(this.api.patch<MeUser>('/users/me', patch));
  }

  /**
   * `me()` but retries `ProfileNotReadyError` up to `maxAttempts` times with
   * a linear back-off. Mirrors the legacy app's `_getUserWithRetry` helper.
   */
  async meWithRetry(maxAttempts = 5, delayMs = 1_000): Promise<MeUser> {
    let lastError: unknown = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await lastValueFrom(
          defer(() => this.api.get<MeUser>(`/users/me${joinQuery(expand(['country']))}`)).pipe(
            catchError((err: unknown) => {
              if (err instanceof ProfileNotReadyError) return throwError(() => err);
              if (err instanceof HttpErrorResponse && err.status === 404) {
                return throwError(() => new ProfileNotReadyError(err));
              }
              return throwError(() => err);
            }),
          ),
        );
      } catch (err) {
        lastError = err;
        if (!(err instanceof ProfileNotReadyError) || attempt === maxAttempts) {
          throw err;
        }
        await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
      }
    }
    throw lastError;
  }
}
